import { Component, OnInit, output } from '@angular/core';
import { LineChart, LineChartOptions } from '../../components/line-chart/line-chart';
import { Card } from '../../components/card/card';
import { BarChart, BarChartOptions } from '../../components/bar-chart/bar-chart';
import { TefBoxService } from '../../services/tef-box-service';
import { formatCompactDateTime } from '../../utils/formatters';
import { SelectButton } from '../../components/select-button/select-button';
import { Loading } from '../../components/loading/loading';

@Component({
  selector: 'app-dashboard',
  imports: [LineChart, Card, BarChart, SelectButton, Loading],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements OnInit {
  loading = true;
  valor = 0;
  pedidosPagos = 0;
  mediaPedidos = '';

  barCategories = ['teste'];
  barData = [0];

  lineCategories = ['teste'];
  lineData = [0];

  today = new Date();

  dataRange: number[] = [];

  selectedRange: 'today' | 'week' | 'month' = 'today';

  lineChartOptions!: LineChartOptions;
  barChartOptions!: BarChartOptions;

  constructor(private tefBoxService: TefBoxService) {}

  ngOnInit(): void {
    this.getDataChart();
  }

  formatterValue(valor: number) {
    return Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  }

  changeValue(valor: number) {
    this.valor = valor;
  }

  changePedidosPagos(resumo: { pedidos: number; media: number }) {
    this.pedidosPagos = resumo.pedidos;
    this.mediaPedidos = Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      resumo.media / 100
    );
  }

  onRangeChange(range: 'today' | 'week' | 'month') {
    this.selectedRange = range;
    this.getDataChart();
  }

  updateCharts() {
    this.lineChartOptions = {
      series: [
        {
          name: 'Faturamento',
          data: this.lineData,
        },
      ],
      chart: {
        type: 'line',
        height: 350,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      annotations: {
        xaxis: [
          {
            x: this.obterDataInfo(this.selectedRange),
            strokeDashArray: 683.3774545748418,
            borderColor: '#FDCED1',
            label: {
              borderColor: '#FDCED1',
              style: {
                color: '#000',
                background: '#FDCED1',
              },
              text: 'Ontem',
            },
          },
        ],
      },
      xaxis: {
        categories: this.lineCategories,
      },
      yaxis: {
        labels: {
          formatter: (value) => {
            return Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
              value / 100
            );
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
        colors: ['#F47920'],
      },
      tooltip: {
        y: {
          formatter: (value) =>
            Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100),
        },
        marker: {
          fillColors: ['#F47920'],
        },
      },
    };

    this.barChartOptions = {
      series: [
        {
          name: 'Valor',
          data: this.barData,
        },
      ],
      chart: {
        type: 'bar',
        height: 300,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: 30,
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: this.barCategories,
      },
      yaxis: {
        labels: {
          formatter: (value) => {
            return Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
              value / 100
            );
          },
        },
      },
      colors: ['#F47920'],
    };
  }

  getDataChart() {
    this.loading = true;

    switch (this.selectedRange) {
      case 'today': {
        const startOfDay = new Date(this.today);
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date(this.today);
        endOfDay.setHours(23, 59, 59, 999);

        this.dataRange = [formatCompactDateTime(startOfDay), formatCompactDateTime(endOfDay)];
        break;
      }

      case 'week': {
        const dayOfWeek = this.today.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Ajusta domingo para ser o último dia
        const startOfWeek = new Date(this.today);
        startOfWeek.setDate(this.today.getDate() + diffToMonday);
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        this.dataRange = [formatCompactDateTime(startOfWeek), formatCompactDateTime(endOfWeek)];
        break;
      }

      case 'month': {
        const startOfMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);

        const endOfMonth = new Date(this.today.getFullYear(), this.today.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);

        this.dataRange = [formatCompactDateTime(startOfMonth), formatCompactDateTime(endOfMonth)];
        break;
      }

      default:
        console.warn('Opção inválida');
    }

    this.tefBoxService.getResumoGanhos(this.dataRange).subscribe({
      next: (resp: any) => {
        this.lineData = resp.ganho;
        this.lineCategories = resp.categories;
        this.pedidosPagos = resp.pedidos;
        this.mediaPedidos = Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(resp.media / 100);
        this.valor = (resp.ganho as Array<number>).reduce((acc, current) => acc + current, 0) / 100;

        this.barData = resp.alternativosValores;
        this.barCategories = resp.alternativosCat;

        this.updateCharts();

        this.loading = false;
      },
      error: () => {
        this.lineData = [0];
        this.lineCategories = ['0'];
        this.pedidosPagos = 0;
        this.mediaPedidos = '0';
        this.valor = 0;

        this.barData = [0];
        this.barCategories = ['0'];

        this.updateCharts();

        this.loading = false;
      },
    });
  }

  private obterDataInfo(periodo: string): string {
    const agora = new Date();
    const ontem = new Date(agora);
    ontem.setDate(agora.getDate() - 1); // Subtrai 1 dia

    switch (periodo.toLowerCase()) {
      case 'today':
        return ontem.toLocaleTimeString('pt-BR'); // Hora de ontem nesse mesmo horário

      case 'week':
        const diasSemana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
        return diasSemana[ontem.getDay()]; // Dia da semana de ontem

      case 'month':
        const dia = ontem.getDate().toString().padStart(2, '0');
        const mes = (ontem.getMonth() + 1).toString().padStart(2, '0');
        return `${dia}/${mes}`; // Data de ontem no formato dd/mm

      default:
        return 'Parâmetro inválido. Use "today", "week" ou "month".';
    }
  }
}
